import { useState } from "react";
import { useContent } from "../contexts/contentContext/contentContext.js";
import useInbox from "hooks/useInbox.ts";
import { useModal } from "../contexts/modalContext/modalContext.js";
import { useUser } from "../contexts/userContext/userContext.js";
import { useToasts } from "react-toast-notifications/dist/ToastProvider.js";
import apiRequest from "../utils/ApiRequest/apiRequest.js";
import SaveMail from "../components/Reusable/SaveMail/SaveMail.js";
import NotEnoughSpaceModal from "../components/Reusable/SaveMail/NotEnoughSpaceModal.js";
import ConfirmationModal from "../components/Reusable/ConfirmationModal/ConfirmationModal.js";
import { useAppSelector } from "store/hooks.ts";
import { selectFParam, selectTParam } from "store/selectors/inboxSelectors.ts";

export const useSaveInboxMail = ({ mailData }) => {
  const { setModal, closeModal } = useModal();
  const { setMailDataProp } = useInbox();
  const { getTextByKey } = useContent();
  const { isSubscribed, updateUserUsedStorage, getUserData } = useUser();
  const { addToast } = useToasts();
  const pageType = useAppSelector(selectTParam);
  const pageCategory = useAppSelector(selectFParam);

  const [loading, setLoading] = useState(false);

  const isSaved =
    pageCategory === "jform" ? mailData?.isSaved : mailData?.isSavedByUser;

  const saveMailAsSubscriber = async () => {
    setLoading(true);
    const params = {
      MailId: mailData.id,
      IsUnsave: isSaved,
      isCollection: false,
    };
    const response = await apiRequest(
      "mails",
      "ReactSaveMailAsSubscriber",
      params,
      "POST"
    );
    if (response.status === "success") {
      // update used storage by user locally

      // Forms already saved, so no need to update used storage
      if (!mailData.isJForm) {
        updateUserUsedStorage({
          UserUsedStorage: isSaved
            ? getUserData().UserUsedStorage - mailData.filesSize
            : getUserData().UserUsedStorage + mailData.filesSize,
        });
      }

      if (Date.parse(response.data)) {
        if (pageCategory === "jform") {
          if (mailData.expirationDate) {
            setMailDataProp(
              mailData.id,
              ["expirationDate", "isSaved"],
              [response.data, false]
            );
          } else {
            setMailDataProp(mailData.id, ["isSaved"], [false]);
          }
        } else {
          setMailDataProp(
            mailData.id,
            ["mailStatus", "expirationDate", "isSaved", "isSavedByUser"],
            ["active", response.data, false, false]
          );
        }
      } else if (isSaved) {
        setMailDataProp(mailData.id, ["isSavedByUser"], [false]);
      } else {
        if (pageCategory === "jform") {
          setMailDataProp(mailData.id, ["isSaved"], [true]);
        } else {
          setMailDataProp(
            mailData.id,
            ["mailStatus", "expirationDate", "isSaved", "isSavedByUser"],
            ["saved", null, true, true]
          );
        }
      }
    }
    setLoading(false);
    return response;
  };

  const saveMailByCode = async (code) => {
    const params = {
      SaveByCodeEncryptedMailId: mailData.id,
      SaveByCodeCodeKey: code,
    };

    const response = await apiRequest(
      "Mails",
      "ReactClientSaveMailByCode",
      params,
      "POST"
    );
    if (response.status === "success") {
      setMailDataProp(
        mailData.id,
        ["mailStatus", "expirationDate"],
        [isSaved ? "active" : "saved", response.data]
      );
    }
    return response;
  };

  const showSaveModal = () => {
    setModal({
      modal: SaveMail,
      modalSize: "full-modal",
      props: {
        isMailSender: pageType === "sent",
        saveMailByCode,
        closeElement: closeModal,
      },
    });
  };

  const saveActionFunc = async () => {
    if (isSubscribed() || isSaved) {
      const response = await saveMailAsSubscriber();
      if (response.status === "success") {
        const isJForm = mailData.isJForm;
        addToast(
          getTextByKey(
            `${isSaved ? "un" : ""}save-mail-success${isJForm ? "-jform" : ""}`
          ),
          {
            appearance: isSaved ? "info" : "success",
          }
        );
      } else {
        if (response.data === "no-space") {
          setModal({ modal: NotEnoughSpaceModal });
        } else {
          addToast(getTextByKey(`please-contact-support`), {
            appearance: "warning",
          });
        }
      }
      return response;
    } else {
      showSaveModal();
    }
  };

  const handleClick = async () => {
    if (pageType === "sent" && isSaved) {
      setModal({
        modal: ConfirmationModal,
        props: {
          confirmTitle: getTextByKey("confirm-unsave-mail-title"),
          confirmText: getTextByKey("confirm-unsave-mail-text"),
          yesButtonText: getTextByKey("confirm-unsave-mail-button-yes"),
          noButtonText: getTextByKey("confirm-unsave-mail-button-no"),
          confirmAction: () => saveActionFunc(),
        },
      });
    } else {
      await saveActionFunc();
    }
  };

  return { handleClick, isSaved, loading };
};
